import React, { ReactNode, useEffect, useRef, useState, Children, createElement } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import { CSSProperties } from 'styled-components';

type Props = {
  fixed?: boolean;
  children: ReactNode;
  as?: keyof React.JSX.IntrinsicElements;
  style?: CSSProperties;
};

/**
 * Functions as a normal div (or react element)
 * until the `fixed` prop is set to `true`
 * then fixes to the top of its parent container and
 * pushes any content underneath below itself.
 *
 * Use this component for when the fixed content height is not known ahead of time.
 *
 * Component will also not be rendered unless children are also renderable (useful for lazy loaded content)
 */
export function FixableContainer({ fixed, children, as = 'div', style }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [contentOffset, setContentOffset] = useState(0);
  const numChildren = Children.toArray(children).length;
  const Container = createElement(
    as,
    {
      ref,
      style: {
        width: '100%',
        ...style,
        ...(fixed && { position: 'fixed', zIndex: 100 }),
      },
    },
    children,
  );

  useEffect(() => {
    if (numChildren < 1 || !fixed || !ref.current) return;

    const observer = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      // Only care about the first element
      const { height } = entries[0].contentRect;
      setContentOffset(height);
    });

    observer.observe(ref.current);

    return () => observer?.disconnect();
  }, [fixed, ref.current, numChildren]);

  return numChildren > 0 ? (
    <>
      {Container}
      {/* push content below fixed portion */}
      {fixed && <div style={{ marginTop: `${contentOffset}px` }} />}
    </>
  ) : null;
}
